import {
  AuthActions,
  AuthSelectors,
  ExperimentsActions,
  FirebaseActionTypes,
  FirebaseActions,
  NotificationActions,
} from '@web_opxp/features';
import { AxiosResponse } from 'axios';
import { all, call, fork, put, select, take, takeLeading } from 'redux-saga/effects';
import { ApiService } from 'web_core_library';
import { detectLandingPageTracking } from '../base/cookieService';
import ENV from '../base/env';
import * as HistoryService from '../base/historyService';
import { getCurrentSearchParams, redirectLogin } from '../base/historyService';
import OrdersService from './ordersService';
import { OrdersActions } from './slice';
import { IOrder, IOrdersRequestPayload } from './types';

export default function* watchOrdersApp() {
  yield all([takeLeading(OrdersActions.startOrdersApp, handleAppStart)]);
}

export function* handleAppStart() {
  // Init ApiService
  yield call(ApiService.init, ENV.ApiUrl);

  // init firebase
  const searchParams: URLSearchParams = yield call(getCurrentSearchParams);
  yield put(
    FirebaseActions.firebaseInitAction(
      ENV.FirebaseConfig.apiKey,
      ENV.FirebaseConfig.projectId,
      ENV.FirebaseConfig.appId,
      ENV.FirebaseConfig.messagingSenderId,
      ENV.FirebaseConfig.measurementId,
      searchParams
    )
  );
  yield take(FirebaseActionTypes.FIREBASE_INITIALIZED);
  yield call(detectLandingPageTracking);
  yield put(FirebaseActions.firebaseStartAction({ analytics: true, config: true, messaging: false }));

  // start session token restoration
  yield put(
    AuthActions.authRestore({
      getCurrentSearchParams: HistoryService.getCurrentSearchParams,
      exchangeAccessToken: HistoryService.exchangeAccessToken,
      updateSearchParams: HistoryService.updateSearchParams,
      refreshToken: HistoryService.refreshToken,
      redirectLogin: HistoryService.redirectLogin,
      redirectLogout: HistoryService.redirectLogout,
    })
  );
  const authResult = yield take([AuthActions.restoreAuthSuccess, AuthActions.restoreAuthFail]);
  if (authResult.type !== AuthActions.restoreAuthSuccess.type) {
    yield call(redirectLogin);
    return;
  }
  const userId = yield select(AuthSelectors.getUserId);
  // set user id for tracking
  yield put(FirebaseActions.setUserAction(userId));
  // load experiments feature as soon as possible
  yield call(loadExperimentsFeature);
  // init service
  yield call(OrdersService.init, ApiService);
  yield fork(handleGetOrders);
  yield put(OrdersActions.ordersAppReady());
}

export function* loadExperimentsFeature() {
  const experiments = [];
  yield put(ExperimentsActions.initExperiments({ experiments }));
  yield take(ExperimentsActions.experimentsReady);
}

export function* handleGetOrders() {
  let orders: IOrder[] = [];
  try {
    const response: AxiosResponse<IOrdersRequestPayload> = yield call(OrdersService.getOrders);
    orders = response.data.orders;
  } catch (error) {
    yield put(NotificationActions.showError((error as Error).message));
  }
  yield put(OrdersActions.updateOrders(orders));
}
